import {apiConstants, postGeneric, getGeneric, postMultipart} from '../../../../common/constants'
import {getMerchant} from '../../../../helpers/ReduxHelpers'

/**
 * Weight Freeze (v2) — the live-data seam for the two sections that are live (T19: "Requests &
 * statuses" and "Raise a request"). Everything the module knows about the backend is in this file
 * and in ./mapRequest.js: the endpoints are proship_mongo's WeightFreezeV2Controller under
 * /api/v2/wf; nothing else in the module imports from here. Catalogue, packing and DWS stay on the
 * frozen dataset and say so on screen.
 */

const PAGE = 200
// 25 pages × 200 = 5,000 requests — far beyond any merchant's book. A hard stop, never a target.
const MAX_PAGES = 25

/** Pulls the server's own sentence out of an axios failure, or falls back. */
export function serverMessage(err, fallback) {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data === 'string' && data) return data
    if (data.error) return data.error
    if (data.message) return data.message
    if (Array.isArray(data.errors) && data.errors.length) return data.errors[0]
  }
  return (err && err.message) || fallback
}

async function write(fn, fallback) {
  try {
    const res = await fn()
    return res && res.data
  } catch (err) {
    throw new Error(serverMessage(err, fallback))
  }
}

/**
 * The merchant this panel is showing: the SESSION's merchant (getMerchant), the same id every other
 * merchant-panel module sends with its requests — the v1 Weight Disputes page, Orders, Dashboard, NDR.
 *
 * The server's own default is the wrong merchant for Prozo staff. They sign in with admin-role logins,
 * and picking a merchant (auth/SelectLogin.js) changes only the session — the token keeps their HOME
 * merchant. So a read that names no merchant answers with every merchant (the admin-wide scope) or with
 * the home merchant (reads that fall back to the token), never the one on screen. The session is also
 * right under the cross-account switch grant, which updates it (hooks/useMerchantSwitcher.js). The server
 * still RBAC-checks the id; a merchant's own login sends its own merchant and lands where it always did.
 */
export function actingMerchantOid() {
  try {
    const merchant = getMerchant()
    const oid = merchant && merchant.id
    return oid && String(oid).trim() ? String(oid).trim() : null
  } catch (err) {
    return null
  }
}

/** Query params naming the panel's merchant, for the reads — see actingMerchantOid. */
function merchantParams() {
  const merchantId = actingMerchantOid()
  return merchantId ? {merchantId} : {}
}

/** `url` with the panel's merchant named on it. */
function scoped(url) {
  const merchantId = actingMerchantOid()
  if (!merchantId) return url
  return url + (url.includes('?') ? '&' : '?') + 'merchantId=' + encodeURIComponent(merchantId)
}

/**
 * A GET that resolves to the PAYLOAD, not the axios response.
 *
 * getGeneric hands back the whole axios response, so returning it straight from a reader leaves
 * every caller to remember `.data` — and the mappers here take payloads. Passing the response
 * object instead reached mapPacks as a non-array and threw "(rows || []).map is not a function",
 * which the page reported as "Could not load your catalogue". Same unwrapping as write(), and the
 * same server-sentence error, so a failure says what the server actually said rather than
 * "Request failed with status code 400".
 */
async function read(url, fallback) {
  try {
    const res = await getGeneric(url)
    return res && res.data
  } catch (err) {
    throw new Error(serverMessage(err, fallback))
  }
}

/** Every request of the panel's merchant, raw backend shape. */
export async function fetchAllRequests() {
  const out = []
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await getGeneric(apiConstants.WF_V2_REQUESTS, {status: 'all', limit: PAGE, skip: page * PAGE, ...merchantParams()})
    const rows = (res && res.data) || []
    rows.forEach((r) => out.push(r))
    if (rows.length < PAGE) break
  }
  return out
}

/** POST /requests with a RaiseBody (see mapRequest.js#toRaiseBody). Resolves to the raw saved request. */
/**
 * The merchant's catalogue as the panel renders it. Paged: ask for a page, the server says whether
 * another exists rather than making the panel count first.
 */
export function apiCatalogue({q, limit = 200, skip = 0} = {}) {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  params.set('limit', String(limit))
  params.set('skip', String(skip))
  const merchantId = actingMerchantOid()
  if (merchantId) params.set('merchantId', merchantId)
  return read(`${apiConstants.WF_V2_BASE}/catalogue?${params.toString()}`, 'Could not read your catalogue')
}

/** Categories and sub-categories with SKU counts, as typed in the merchant's own product master. */
export function apiSubcategories() {
  return read(scoped(`${apiConstants.WF_V2_BASE}/subcategories`), 'Could not read your categories')
}

/** Packing materials, with the packaging's own weight where the merchant has recorded it. */
export function apiPacks() {
  return read(scoped(`${apiConstants.WF_V2_BASE}/packs`), 'Could not read your packing materials')
}

export function apiRaiseRequest(body) {
  // Attached here rather than in toRaiseBody: that mapper is deliberately pure and import-light, and
  // reaching for the session from inside it would drag the redux store into its unit tests.
  const merchantId = body && body.merchantId ? body.merchantId : actingMerchantOid()
  const withMerchant = merchantId ? {...body, merchantId} : body
  return write(() => postGeneric(apiConstants.WF_V2_REQUESTS, withMerchant), 'Could not raise the freeze request')
}

/**
 * Multipart evidence: one packing video (store-once) and up to five stills. Only real File objects
 * are sent — the demo harness's name/url placeholders carry no bytes. Resolves to the raw updated
 * request, or null when there was nothing to upload.
 */
export function apiUploadEvidence(id, files) {
  const form = new FormData()
  let any = false
  const hasFile = typeof File !== 'undefined'
  if (files && hasFile && files.video instanceof File) { form.append('video', files.video); any = true }
  ;(files && files.images ? files.images : []).forEach((f) => {
    if (hasFile && f instanceof File) { form.append('images', f); any = true }
  })
  if (!any) return Promise.resolve(null)
  return write(() => postMultipart(`${apiConstants.WF_V2_REQUESTS}/${id}/evidence`, form), 'Evidence upload failed')
}

/** The merchant's answer to a Prozo ask: {kindLabel, ships, note} — RqSubmitDrawer's own payload, unchanged. */
export function apiSubmitAdditionalData(id, payload) {
  const p = payload || {}
  return write(() => postGeneric(`${apiConstants.WF_V2_REQUESTS}/${id}/submit`, {
    kindLabel: p.kindLabel, ships: Number(p.ships) || 0, note: p.note,
  }), 'Could not send the additional data')
}

export function apiWithdrawRequest(id, remark) {
  return write(() => postGeneric(`${apiConstants.WF_V2_REQUESTS}/${id}/withdraw`, {remark}), 'Could not withdraw the request')
}

/** The merchant agrees to Prozo's proposed weight — it becomes the approved freeze (billing still inert). */
export function apiAcceptProposal(id) {
  return write(() => postGeneric(`${apiConstants.WF_V2_REQUESTS}/${id}/accept-proposal`, {}), 'Could not accept the proposal')
}

export function apiDeclineProposal(id, remark) {
  return write(() => postGeneric(`${apiConstants.WF_V2_REQUESTS}/${id}/decline-proposal`, {remark}), 'Could not decline the proposal')
}

/** Item 6 — the merchant's own categories as typed, the blanks, and whether category-level freezes are available. */
export async function apiCategoryReport() {
  const res = await getGeneric(`${apiConstants.WF_V2_BASE}/category-report`, merchantParams())
  return res && res.data
}
